/**
 * CareYearTimeline — gemeinsame Gantt-artige Jahrestimeline für Betreuungen.
 *
 * Extrahiert aus BetreuungenTab.tsx, damit die Komponente in BEIDEN genutzt
 * werden kann:
 *   - BetreuungenTab  (Betreuungen-Reiter)
 *   - PbDashboard     (Jahresübersicht-Karte)
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/ui';
import { BRAND, BRAND_DARK, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import type { V1Absence } from '../../../lib/v1';
import i18n from '../../../i18n';
import { formatDate } from '../../../lib/dateFormat';

import {
  ABSENCE_COLOR, PAUSE_COLOR, TASK_COLOR, VERORDNUNG_COLOR,
  STATUS_TONE, OVERLAY_OPACITY, MARKER_OPACITY, ABSENCE_ROW,
} from './types';
import type { CareRow, TimelineTask, TimelineVerordnung } from './types';
import {
  clamp, dayOfYear, daysInYear, parseDate,
  buildYearOptions, buildTimeline, buildAbsenceBars, buildTaskMarkers,
  buildVerordnungMarkers, getMonthAbbreviations,
  statusLabel,
} from './helpers';
import { TaskMarkerView } from './TaskMarkerView';
import { AbsenceBarView } from './AbsenceBarView';
import { TimelineBarView } from './TimelineBarView';
import { BK_AVAILABILITY_TYPES } from '../../../lib/bkAvailability';

export type { CareRow, TimelineTask, TimelineVerordnung };
export type { TimelineBar, AbsenceTimelineBar, TaskMarker } from './types';
export { ABSENCE_COLOR, PAUSE_COLOR, TASK_COLOR, STATUS_TONE };
export {
  statusLabel, parseDate, startOfDay, dayOfYear, daysInYear,
  diffDays, clamp, buildYearOptions, buildTimeline, summarizeCares,
  buildAbsenceBars, buildTaskMarkers, getMonthAbbreviations,
} from './helpers';

export interface CareYearTimelineProps {
  /** Full list of cares — filtering by year is done internally. */
  cares: CareRow[];
  year: number;
  onYearChange: (year: number) => void;
  dark: boolean;
  /** 1 = BK view (shows PB names), 2 = PB view (shows BK names). */
  userRole?: number;
  emptyLabel?: string;
  /** Absences — Vertragspause in der Betreuungszeile, Krankenhaus in Zeile 2. */
  absences?: V1Absence[];
  /** Aufgaben/Todos mit Deadline (zieldatum) — als Marker im oberen Band (T1). */
  tasks?: TimelineTask[];
  /** Batch-12: Verordnungen mit Termin — blaue Marker + Legende. */
  verordnungen?: TimelineVerordnung[];
  /** Click a care bar → open edit. */
  onCareClick?: (care: CareRow) => void;
  /** Click an absence bar → open edit (receives the absence id). */
  onAbsenceClick?: (absenceId: number) => void;
  /** Click a task deadline marker → open task (receives the task id). */
  onTaskClick?: (taskId: number) => void;
  /** Batch-12: Click auf einen Verordnungs-Marker (erhält die Verordnungs-ID). */
  onVerordnungClick?: (verordnungId: number) => void;
}

export function CareYearTimeline({
  cares,
  year,
  onYearChange,
  dark,
  userRole = 2,
  emptyLabel,
  absences = [],
  tasks = [],
  verordnungen = [],
  onCareClick,
  onAbsenceClick,
  onTaskClick,
  onVerordnungClick,
}: CareYearTimelineProps) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);

  const resolvedEmptyLabel = emptyLabel ?? t('pflege.cares.timeline_empty');
  const months = getMonthAbbreviations(i18n.language);

  const years = buildYearOptions(cares, year);
  const bars = buildTimeline(cares, userRole, year, (k, opts) => i18n.t(k, opts));
  // Höchstens zwei Zeilen: Zeile 0 = Betreuungen (+ Vertragspausen als
  // halbtransparente Unterbrechung), Zeile 1 = Krankenhaus-Abwesenheiten.
  const absenceBars = buildAbsenceBars(absences, year, ABSENCE_ROW, t('pflege.absences.title'), t, 0);
  /** Überdeckt die Abwesenheit eine Betreuung derselben Zeile? Dann ohne eigene Beschriftung. */
  const overlapsCare = (a: { left: number; width: number; row: number }) =>
    bars.some(b => b.row === a.row && a.left < b.left + b.width && b.left < a.left + a.width);
  const fmtTaskDt = (v: unknown) => {
    const d = parseDate(v);
    return d ? formatDate(d) : String(v ?? '—');
  };
  const taskMarkers = buildTaskMarkers(tasks, year, fmtTaskDt);
  // Batch-12: Verordnungs-Termine als blaue Diamant-Marker.
  const verordnungMarkers = buildVerordnungMarkers(verordnungen, year, fmtTaskDt);

  const currentYear = new Date().getFullYear();
  const todayPct =
    currentYear === year
      ? clamp((dayOfYear(new Date()) / daysInYear(year)) * 100, 0, 100)
      : null;

  const maxRow = Math.max(-1, ...bars.map(b => b.row), ...absenceBars.map(b => b.row));
  const taskBand = (taskMarkers.length || verordnungMarkers.length) ? 28 : 0;
  const height = Math.max(110, 54 + (maxRow + 1) * 30) + taskBand;

  return (
    <div>
      {/* ── Jahres-Selector ─────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: '8px 16px', borderBottom: `1px solid ${tc.border}`,
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: tc.text }}>
          {t('pflege.cares.year')}
          <select
            aria-label={t('pflege.cares.year_select')}
            value={year}
            onChange={e => onYearChange(Number(e.target.value))}
            style={{
              border: `1px solid ${tc.border}`, borderRadius: 9,
              background: tc.inputBg, color: tc.heading, padding: '8px 10px',
            }}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      </div>

      {/* ── Gantt-Körper ─────────────────────────────────────────────── */}
      <div style={{ padding: 16 }}>
        <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
          <div style={{
            position: 'relative', minWidth: 880, height,
            border: `1px solid ${tc.borderSoft}`, borderRadius: 14,
            background: tc.cardMuted,
          }}>
            {/* Month grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', height: '100%' }}>
              {months.map(m => (
                <div
                  key={m}
                  style={{
                    borderLeft: `1px solid ${tc.borderSoft}`,
                    padding: '9px 8px', color: tc.muted, fontSize: 11, fontWeight: 700,
                  }}
                >
                  {m}
                </div>
              ))}
            </div>

            {/* Today marker */}
            {todayPct !== null && (
              <div style={{
                position: 'absolute', top: 0, bottom: 0, left: `${todayPct}%`,
                width: 2, background: BRAND, boxShadow: `0 0 0 3px ${BRAND_LIGHT}`,
              }}>
                <span style={{
                  position: 'absolute', top: 7, left: 7,
                  color: BRAND_DARK, background: '#fff',
                  border: `1px solid ${BRAND_LIGHT}`, borderRadius: 999,
                  padding: '2px 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {t('kalender.action.today')}
                </span>
              </div>
            )}

            {/* Empty state */}
            {bars.length === 0 && absenceBars.length === 0 && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: tc.muted, fontSize: 13,
              }}>
                {resolvedEmptyLabel}
              </div>
            )}

            {/* Timeline bars */}
            {bars.map(b => (
              <TimelineBarView
                key={b.care.id}
                bar={b}
                dark={dark}
                onClick={onCareClick ? () => onCareClick(b.care) : undefined}
              />
            ))}
            {/* Absence bars — Vertragspause in Zeile 0 (halbtransparent), Krankenhaus in Zeile 1 */}
            {absenceBars.map(b => (
              <AbsenceBarView
                key={`abs-${b.id}`}
                bar={b}
                dark={dark}
                showLabel={!overlapsCare(b)}
                overlay={b.row === 0}
                onClick={onAbsenceClick ? () => onAbsenceClick(b.id) : undefined}
              />
            ))}
            {/* Aufgaben-Deadline-Marker (T1) — oberes Band */}
            {taskMarkers.map(m => (
              <TaskMarkerView
                key={`task-${m.id}`}
                marker={m}
                dark={dark}
                onClick={onTaskClick ? () => onTaskClick(m.id) : undefined}
              />
            ))}
            {/* Batch-12: Verordnungs-Termin-Marker (blau) */}
            {verordnungMarkers.map(m => (
              <TaskMarkerView
                key={`verordnung-${m.id}`}
                marker={m}
                dark={dark}
                color={VERORDNUNG_COLOR}
                onClick={onVerordnungClick ? () => onVerordnungClick(m.id) : undefined}
              />
            ))}
          </div>
        </div>

        {/* Status legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, alignItems: 'center' }}>
          {[0, 3, 2, 1, 4].map(s => (
            <Badge key={s} dark={dark} tone={STATUS_TONE[s]}>
              {statusLabel(s, k => i18n.t(k))}
            </Badge>
          ))}
          {/* BK-Profil: je Verfügbarkeits-Art ein eigener Legenden-Eintrag */}
          {userRole === 1 && BK_AVAILABILITY_TYPES.filter(d => absenceBars.some(b => b.grundRaw === d.grund)).map(d => (
            <span key={d.type} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: tc.muted }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: d.color, display: 'inline-block' }} />
              {t(d.labelKey)}
            </span>
          ))}
          {absenceBars.some(b => !['Pause', 'Vertragspause'].includes(b.grundRaw)
            && !(userRole === 1 && BK_AVAILABILITY_TYPES.some(d => d.grund === b.grundRaw))) && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: tc.muted }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: ABSENCE_COLOR, display: 'inline-block' }} />
              {t('pflege.absences.title')}
            </span>
          )}
          {absenceBars.some(b => ['Pause', 'Vertragspause'].includes(b.grundRaw)) && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: tc.muted }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: PAUSE_COLOR, opacity: OVERLAY_OPACITY, display: 'inline-block' }} />
              {t('pflege.absences.grund_options.vertragspause')}
            </span>
          )}
          {taskMarkers.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: tc.muted }}>
              <span style={{ width: 11, height: 11, borderRadius: 2, background: TASK_COLOR, opacity: MARKER_OPACITY, transform: 'rotate(45deg)', display: 'inline-block' }} />
              {t('pflege.detail.dashboard.timeline_tasks_legend')}
            </span>
          )}
          {verordnungMarkers.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: tc.muted }}>
              <span style={{ width: 11, height: 11, borderRadius: 2, background: VERORDNUNG_COLOR, opacity: MARKER_OPACITY, transform: 'rotate(45deg)', display: 'inline-block' }} />
              {t('pflege.verordnungen.legend')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
